/**
 * collect 화면의 흐름 로직 (TASK-2026-004 SPEC 「포함 범위」 2·3·6, AC-01·02·04).
 *
 * 화면(React)과 분리한 이유는 검사 때문이다. 동의·초대코드·Q1·응답·최종 제출은 실패
 * 경로가 많고, 그 판정이 옳은지는 DOM 없이도 확인할 수 있어야 한다. 여기 있는 함수는
 * 모두 "무엇을 저장했고 무엇이 막혔는가"를 값으로 돌려준다.
 *
 * 규칙:
 * - 동의 전에는 캠페인 안내와 기존 세션 확인 외에 어떤 요청도 만들지 않는다(OF1-02,
 *   TASK-2026-011 AC-08). 세션 생성 intent 발급이 동의 뒤 첫 쓰기 요청이다.
 * - 세션 생성의 503·통신 오류는 거부가 아니다(CR-01 §4). 같은 intent로 다시 보내고, 결과를
 *   확정하지 못하면 `결과 확인 중`으로 두고 회수(recover)한다. 초대코드는 메모리에만 둔다.
 * - Q1은 한 번 저장하면 잠긴다. 이후 응답 저장이 Q1 값을 덮지 않는다(F1-04.3).
 * - 최종 제출은 서버가 확정했을 때만 완료로 기록한다. 409 `incomplete`는 사유를 그대로
 *   돌려주고 완료로 바꾸지 않는다(F1-04.8).
 * - 응답 초안은 참가자 기기에만 남기고, 제출이 확정되면 지운다.
 */

import { ApiError } from '../online/client';
import type { Api } from '../online/api';
import type { PublicConfig, SessionCreated, SessionState } from '../online/client';
import { verifyResultText, type VerifiedResult } from '../online/result';
import {
  clearSessionState,
  readSessionState,
  writeSessionState,
  type OnlineSessionState,
  type StateStorage,
} from '../online/state';
import { checkAnswers, Q1_ID, toPayload, type AnswerIssue, type AnswerValue, type Answers } from './survey';

export const SESSION_SCHEMA = 'ccatlas.online-session/1';

/**
 * Q1 원문을 이 기기에서 잃었을 때 함께 보내는 표식.
 *
 * 응답 문서는 version마다 전체가 덮이므로, 초안을 잃은 뒤 최종 제출을 하면 Q1이 빈 답으로
 * 덮인다. 그것을 "응답하지 않음"과 구별하기 위해 사실을 한 칸으로 남긴다. 분석은 이 표식이
 * 있는 세션의 Q1을 결측이 아니라 **유실**로 다뤄야 한다.
 */
export const Q1_LOST_KEY = 'q1_answer_lost';

export interface FlowDeps {
  readonly api: Api;
  readonly storage: StateStorage;
  readonly now: () => number;
  /** 재시도 대기. 검사는 기다리지 않는 함수를 넘긴다. */
  readonly sleep?: (ms: number) => Promise<void>;
}

/**
 * 세션 생성 결과(ACK)를 아직 확정하지 못한 표식 (TASK-2026-011 CR-01 §4).
 *
 * intent 토큰은 HttpOnly cookie에만 있고, 초대코드는 화면 메모리에만 있다. 여기에는 어느
 * 캠페인에서 시작했는지만 남겨 새로고침 뒤 같은 intent의 결과를 회수할 수 있게 한다.
 */
export const PENDING_INTENT_KEY = 'ccatlas.online.pending-intent';
export const PENDING_INTENT_SCHEMA = 'ccatlas.online-pending-intent/1';

export interface PendingIntent {
  readonly schema: typeof PENDING_INTENT_SCHEMA;
  readonly campaign_id: string;
  readonly consent_version: string;
  readonly started_at: number;
}

export function readPendingIntent(storage: StateStorage): PendingIntent | undefined {
  try {
    const raw = storage.getItem(PENDING_INTENT_KEY);
    const value = raw === null ? undefined : (JSON.parse(raw) as Partial<PendingIntent>);
    return value?.schema === PENDING_INTENT_SCHEMA &&
      typeof value.campaign_id === 'string' &&
      typeof value.consent_version === 'string' &&
      typeof value.started_at === 'number'
      ? (value as PendingIntent)
      : undefined;
  } catch {
    return undefined;
  }
}

function writePendingIntent(storage: StateStorage, value: PendingIntent): void {
  try {
    storage.setItem(PENDING_INTENT_KEY, JSON.stringify(value));
  } catch {
    // 표식을 남기지 못해도 같은 intent cookie는 남는다. 이 화면 안의 재시도는 그대로 된다.
  }
}

function clearPendingIntent(storage: StateStorage): void {
  try {
    storage.removeItem(PENDING_INTENT_KEY);
  } catch {
    // 지우지 못한 표식은 다음 진입에서 회수가 `없음`으로 정리한다.
  }
}

/** 세션 생성 재시도 간격(지수 backoff). 다 쓰면 `결과 확인 중`으로 넘긴다. */
export const CREATE_RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000];

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** 결과가 확정되지 않은 실패(통신 오류·서버 오류)인가. 거부가 아니므로 같은 intent로 다시 확인한다. */
function ambiguous(error: ApiError): boolean {
  return error.status === 0 || error.status >= 500;
}

export type ConfigOutcome =
  | { readonly kind: 'open'; readonly config: PublicConfig }
  | { readonly kind: 'closed'; readonly config: PublicConfig }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unreachable'; readonly code: string };

export async function loadConfig(deps: FlowDeps, campaignId: string): Promise<ConfigOutcome> {
  try {
    const config = await deps.api.client.publicConfig(campaignId);
    return config.open ? { kind: 'open', config } : { kind: 'closed', config };
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    if (api.status === 404) {
      return { kind: 'not_found' };
    }
    return { kind: 'unreachable', code: api.code };
  }
}

export type RestoreOutcome =
  | { readonly kind: 'none' }
  /**
   * 로컬 표식 없이 cookie로 세션을 확인하려 했지만 통신·서버 오류로 확인하지 못했다
   * (TASK-2026-012 CR-01 REV-002). 세션이 없다는 뜻이 아니다 —— 기존 참여가 있을 수 있으므로
   * 새 참여를 열지 않는다.
   */
  | { readonly kind: 'unreachable'; readonly code: string }
  | { readonly kind: 'active'; readonly session: OnlineSessionState; readonly server: SessionState }
  | { readonly kind: 'gone'; readonly code: string }
  /**
   * URL·로컬 표식·서버 세션이 서로 다른 캠페인이거나 동의 버전이 다르다 (REV-008).
   *
   * 진행도 전송도 하지 않는다. 참가자가 어느 쪽으로 갈지 직접 고르게 한다.
   */
  | {
      readonly kind: 'mismatch';
      readonly reason: 'campaign' | 'consent_version' | 'session';
      /** 지금 보고 있는 링크의 캠페인. */
      readonly requested: string;
      /** 서버가 아는 이 cookie의 캠페인. */
      readonly current: string;
    }
  /** 세션 표식은 있는데 서버에 물어보지 못했다. 그대로 두고 다시 시도한다. */
  | { readonly kind: 'unverified'; readonly session: OnlineSessionState; readonly code: string };

export interface RestoreInput {
  /** 지금 링크가 가리키는 캠페인. */
  readonly campaignId: string;
  /** 지금 캠페인이 요구하는 동의 버전. 모르면 검사하지 않는다. */
  readonly consentVersion?: string;
}

/**
 * 저장된 세션을 되살린다.
 *
 * 서버에 물어보는 것이 기준이다. 로컬 표식만으로 "참여 중"이라고 판단하면, 만료·철회된
 * 세션에서도 화면이 진행 중처럼 보인다.
 *
 * **캠페인과 동의 버전을 대조한다** (REV-008). 링크는 A인데 cookie는 B 세션이면,
 * 화면의 안내·링크·동의 문구는 A이고 저장되는 자료는 B가 된다. 셋이 같을 때만
 * 활성 세션으로 채택한다.
 */
export async function restore(deps: FlowDeps, input: RestoreInput): Promise<RestoreOutcome> {
  const local = readSessionState(deps.storage);
  if (local === undefined) {
    return restoreFromCookie(deps, input);
  }
  if (local.campaign_id !== input.campaignId) {
    return { kind: 'mismatch', reason: 'campaign', requested: input.campaignId, current: local.campaign_id };
  }
  /*
   * 동의 버전은 서버에 묻기 **전에** 대조한다 (R2-REV-003). 저장된 표식과 캠페인 설정만으로
   * 판단할 수 있고, 서버 조회가 일시 실패한 `unverified` 경로에서도 건너뛰면 안 된다.
   */
  if (input.consentVersion !== undefined && local.consent_version !== input.consentVersion) {
    return { kind: 'mismatch', reason: 'consent_version', requested: input.consentVersion, current: local.consent_version };
  }
  try {
    const server = await deps.api.client.session();
    if (server.campaign_id !== input.campaignId) {
      /*
       * cookie가 다른 캠페인의 세션을 가리킨다. CSRF 토큰을 **받아 두지 않는다** ——
       * 토큰이 있으면 그 뒤의 어떤 경로든 실수로 B 세션에 쓸 수 있다.
       */
      return { kind: 'mismatch', reason: 'campaign', requested: input.campaignId, current: server.campaign_id };
    }
    if (server.session_ref !== local.session_ref) {
      /*
       * 같은 캠페인이지만 cookie가 이 기기의 표식과 **다른 참여**를 가리킨다 (R2-REV-003).
       * 표식을 서버 참조로 바꿔 쓰면 이 기기의 초안·큐·기록이 다른 참여에 붙는다.
       */
      return { kind: 'mismatch', reason: 'session', requested: local.session_ref, current: server.session_ref };
    }
    deps.api.setCsrf(server.csrf_token);
    const session: OnlineSessionState = {
      ...local,
      resume_until: server.resume_until,
      response_version: server.response_version,
      submitted: server.completed,
      answers: server.completed ? {} : local.answers,
    };
    writeSessionState(deps.storage, session);
    return { kind: 'active', session, server };
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    if (api.status === 401) {
      clearSessionState(deps.storage);
      return { kind: 'gone', code: api.code };
    }
    if (api.status === 403) {
      return { kind: 'gone', code: api.code };
    }
    return { kind: 'unverified', session: local, code: api.code };
  }
}

/**
 * 로컬 표식이 없을 때 HttpOnly 세션 cookie로 서버 상태를 확인한다 (TASK-2026-011 SPEC §5).
 *
 * - 같은 캠페인의 세션이면 그 참여를 되살린다(이 기기의 초안은 없다).
 * - 다른 캠페인의 진행 중 세션이면 `mismatch`로 멈춘다. 참가자가 원래 참여로 가거나 새로
 *   시작할지 고른다. 같은 코호트면 새로 시작해도 서버가 `cohort_already_joined`로 막는다.
 * - cookie가 없거나 서버가 세션을 거절하면(401·403 등) `none`이다. 쓰기는 하지 않는다.
 * - 통신·서버 오류로 확인하지 못하면 `unreachable`이다(REV-002). 세션 없음으로 바꾸지 않는다.
 */
async function restoreFromCookie(deps: FlowDeps, input: RestoreInput): Promise<RestoreOutcome> {
  let server: SessionState;
  try {
    server = await deps.api.client.session();
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    return ambiguous(api) ? { kind: 'unreachable', code: api.code } : { kind: 'none' };
  }
  if (server.campaign_id !== input.campaignId) {
    return server.status === 'active'
      ? { kind: 'mismatch', reason: 'campaign', requested: input.campaignId, current: server.campaign_id }
      : { kind: 'none' };
  }
  if (input.consentVersion === undefined) {
    return { kind: 'none' };
  }
  deps.api.setCsrf(server.csrf_token);
  const session: OnlineSessionState = {
    schema: SESSION_SCHEMA,
    campaign_id: server.campaign_id,
    session_ref: server.session_ref,
    consent_version: input.consentVersion,
    resume_until: server.resume_until,
    created_at: deps.now(),
    response_version: server.response_version,
    submitted: server.completed,
    answers: {},
    // 응답이 한 번이라도 저장됐으면 Q1은 이미 확정됐다(Q1 저장이 첫 응답이다).
    q1_locked: server.response_version > 0,
  };
  writeSessionState(deps.storage, session);
  return { kind: 'active', session, server };
}

export type StartOutcome =
  | { readonly kind: 'started'; readonly session: OnlineSessionState }
  | { readonly kind: 'rejected'; readonly code: string }
  /** 결과를 아직 확정하지 못했다. 같은 intent로 `recoverPending`을 다시 부른다(CR-01 §4). */
  | { readonly kind: 'pending' }
  /** 같은 코호트에 이미 참여했다. 최초 캠페인으로 안내한다(SPEC §5). */
  | { readonly kind: 'joined'; readonly campaignId: string }
  /**
   * 회수한 세션이 다른 링크(캠페인)에서 만든 것이다. 이 링크의 동의로 채택하지 않고 원래
   * 캠페인으로 안내한다(G3 REV-001).
   */
  | { readonly kind: 'other_campaign'; readonly campaignId: string };

export interface StartInput {
  readonly campaignId: string;
  /** 초대코드 캠페인에서만. 화면 메모리에서만 전달하고 저장하지 않는다. */
  readonly inviteCode?: string;
  readonly consentVersion: string;
}

/**
 * 서버가 확정한 세션을 이 기기의 참여로 기록한다. ACK를 받은 뒤에만 표식을 지운다.
 *
 * 응답의 `campaign_id`가 이 요청의 캠페인과 같을 때만 채택한다(G3 REV-001). A 링크에서 응답을
 * 잃은 intent를 B 링크에서 회수하면 A의 세션이 돌아온다. 이를 B의 동의 버전으로 저장하면 모집
 * 채널 귀속과 동의 증거가 섞이므로 저장하지 않고 A로 안내한다. `campaign_id`가 없는 응답은
 * 어느 캠페인인지 확인할 수 없으므로 채택하지 않는다.
 */
function adopt(deps: FlowDeps, created: SessionCreated, expected: { campaignId: string; consentVersion: string }): StartOutcome {
  if (created.campaign_id !== expected.campaignId) {
    clearPendingIntent(deps.storage);
    return typeof created.campaign_id === 'string' && created.campaign_id !== ''
      ? { kind: 'other_campaign', campaignId: created.campaign_id }
      : { kind: 'rejected', code: 'session_campaign_unverified' };
  }
  deps.api.setCsrf(created.csrf_token);
  const session: OnlineSessionState = {
    schema: SESSION_SCHEMA,
    campaign_id: created.campaign_id,
    session_ref: created.session_ref,
    consent_version: expected.consentVersion,
    resume_until: created.resume_until,
    created_at: deps.now(),
    response_version: 0,
    submitted: false,
    answers: {},
    q1_locked: false,
  };
  writeSessionState(deps.storage, session);
  clearPendingIntent(deps.storage);
  return { kind: 'started', session };
}

/**
 * 동의 뒤 세션을 시작한다 (TASK-2026-011 SPEC §3·§6, CR-01 §1·§4, CR-02 §1).
 *
 * 이 캠페인에 결속된 intent를 받고, 같은 intent·같은 요청으로 세션 생성을 보낸다. 503·통신 오류는
 * 거부로 바꾸지 않고 backoff로 다시 보낸다(서버는 같은 intent에 같은 결과를 돌려준다). 끝내 확정하지
 * 못하면 `pending`을 돌려주고 표식을 남긴다. 확정된 거부는 표식을 지운다.
 *
 * 이 브라우저의 intent가 이미 세션을 만들었거나 다른 캠페인에 결속된 채 결과가 없으면 서버는 새
 * intent를 주지 않는다. 새로 만들지 않고 그 intent의 결과를 먼저 회수한다.
 */
export async function startSession(deps: FlowDeps, input: StartInput): Promise<StartOutcome> {
  const sleep = deps.sleep ?? defaultSleep;
  try {
    await deps.api.client.issueSessionIntent(input.campaignId);
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    if (api.code === 'session_intent_committed' || api.code === 'session_intent_campaign_conflict') {
      // 이 브라우저의 intent로 세션이 이미 만들어졌거나, 다른 캠페인 intent의 결과가 아직 없다. 회수한다.
      return recoverPending(deps, input);
    }
    return { kind: 'rejected', code: api.code };
  }
  writePendingIntent(deps.storage, {
    schema: PENDING_INTENT_SCHEMA,
    campaign_id: input.campaignId,
    consent_version: input.consentVersion,
    started_at: deps.now(),
  });
  for (let attempt = 0; ; attempt += 1) {
    try {
      return adopt(deps, await deps.api.client.createSession(input), input);
    } catch (error) {
      const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
      if (ambiguous(api)) {
        const delay = CREATE_RETRY_DELAYS_MS[attempt];
        if (delay === undefined) {
          return { kind: 'pending' };
        }
        await sleep(delay);
        continue;
      }
      if (api.code === 'session_intent_conflict' || api.code === 'session_intent_campaign_conflict') {
        // intent가 이미 다른 요청·다른 캠페인에 묶였다. 그 결과를 먼저 확정한다.
        return recoverPending(deps, input);
      }
      clearPendingIntent(deps.storage);
      if (api.code === 'cohort_already_joined' && typeof api.detail['campaign_id'] === 'string') {
        return { kind: 'joined', campaignId: api.detail['campaign_id'] };
      }
      return { kind: 'rejected', code: api.code };
    }
  }
}

/**
 * 같은 intent로 commit된 결과를 회수한다 (CR-01 §4). 새 세션을 만들지 않는다.
 *
 * `pending`이면 표식을 그대로 두고 다시 확인할 수 있게 한다. intent가 없거나 만료됐으면
 * 표식을 지우고 `rejected`(`session_intent_required`·`session_intent_expired`)를 돌려준다.
 */
export async function recoverPending(
  deps: FlowDeps,
  input: { readonly campaignId: string; readonly consentVersion: string },
): Promise<StartOutcome> {
  try {
    const recovered = await deps.api.client.recoverSessionIntent();
    if (recovered.pending) {
      return { kind: 'pending' };
    }
    return adopt(deps, recovered, input);
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    if (ambiguous(api)) {
      return { kind: 'pending' };
    }
    clearPendingIntent(deps.storage);
    return { kind: 'rejected', code: api.code };
  }
}

/**
 * 첫 진입에서 지난번에 결과를 확정하지 못한 세션 생성이 있으면 회수한다.
 * 표식이 없거나 다른 캠페인의 표식이면 아무 요청도 하지 않는다(`undefined`).
 */
export async function resumePending(deps: FlowDeps, campaignId: string): Promise<StartOutcome | undefined> {
  const pending = readPendingIntent(deps.storage);
  if (pending === undefined || pending.campaign_id !== campaignId) {
    return undefined;
  }
  return recoverPending(deps, { campaignId: pending.campaign_id, consentVersion: pending.consent_version });
}

export type SaveOutcome =
  | { readonly kind: 'saved'; readonly session: OnlineSessionState }
  | { readonly kind: 'issues'; readonly issues: readonly AnswerIssue[] }
  | { readonly kind: 'failed'; readonly code: string; readonly retriable: boolean };

/** 잠긴 Q1을 덮지 않도록 보낼 값을 고른다. */
function mergeAnswers(session: OnlineSessionState, answers: Answers): Record<string, AnswerValue> {
  const payload: Record<string, AnswerValue> = { ...toPayload(answers) };
  if (!session.q1_locked) {
    return payload;
  }
  const stored = session.answers[Q1_ID];
  if (typeof stored === 'string' && stored.trim() !== '') {
    payload[Q1_ID] = stored;
    return payload;
  }
  if (typeof payload[Q1_ID] === 'string' && (payload[Q1_ID] as string).trim() !== '') {
    /*
     * 잠긴 뒤인데 초안이 사라졌고 화면에는 값이 있다. 플레이 뒤의 답으로 처음 이해를
     * 덮지 않기 위해 값을 싣지 않고, 유실 사실만 남긴다.
     */
    payload[Q1_ID] = null;
    payload[Q1_LOST_KEY] = true;
    return payload;
  }
  if (stored === undefined) {
    payload[Q1_LOST_KEY] = true;
  }
  return payload;
}

/**
 * 응답을 저장한다. `final: true`면 최종본이다.
 *
 * version 충돌은 한 번 다시 맞춘다. 다른 탭이 먼저 저장했거나 재개 뒤 로컬 version이
 * 뒤처진 경우인데, 참가자에게 "충돌"을 보여 줄 이유가 없다.
 */
export async function saveAnswers(
  deps: FlowDeps,
  session: OnlineSessionState,
  answers: Answers,
  options: { readonly final: boolean; readonly lockQ1?: boolean },
): Promise<SaveOutcome> {
  const issues = checkAnswers(answers);
  if (issues.length > 0) {
    return { kind: 'issues', issues };
  }
  const payload = mergeAnswers(session, answers);

  async function put(baseVersion: number) {
    return deps.api.client.putResponse({
      base_version: baseVersion,
      final: options.final,
      answers: payload,
    });
  }

  try {
    let ack;
    try {
      ack = await put(session.response_version);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409 && error.code === 'response_version_conflict') {
        const server = await deps.api.client.session();
        deps.api.setCsrf(server.csrf_token);
        ack = await put(server.response_version);
      } else {
        throw error;
      }
    }
    const next: OnlineSessionState = {
      ...session,
      response_version: ack.version,
      q1_locked: session.q1_locked || options.lockQ1 === true,
      // 초안은 이 기기에만 남는다. 새로고침·오프라인에서 다시 입력하지 않게 한다.
      answers: { ...session.answers, ...answers },
    };
    writeSessionState(deps.storage, next);
    return { kind: 'saved', session: next };
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    return { kind: 'failed', code: api.code, retriable: api.retriable };
  }
}

export type SubmitOutcome =
  | {
      readonly kind: 'complete';
      readonly session: OnlineSessionState;
      readonly digest: string;
      /**
       * 서버가 확정한 단일 envelope (REV-007).
       *
       * 검증에 실패했거나 읽지 못하면 `undefined`다. 그때는 완료로 **표시하지
       * 않는다** —— 서버에 확정본이 있다는 사실과 이 화면이 그것을 확인했다는
       * 사실은 다르다.
       */
      readonly result: VerifiedResult | undefined;
    }
  /** 서버가 아직 확정하지 않았다. 무엇이 막고 있는지 그대로 전달한다. */
  | { readonly kind: 'incomplete'; readonly blocking: readonly string[] }
  | { readonly kind: 'failed'; readonly code: string; readonly retriable: boolean };

/**
 * 확정 결과를 원문으로 읽어 검증한다 (REV-007).
 *
 * 완료 직후와 완료 화면의 `다시 확인`이 같은 경로를 쓴다. 두 자리가 각자 조립하면
 * 화면에 보이는 것과 내려받는 것이 달라질 수 있다.
 *
 * `onError`는 요청 자체의 실패를 알려 준다 (TASK-2026-012 CR-01 REV-002). 화면은 통신
 * 오류만 연결 점검에 넘기고, 검증 실패·거부는 연결 문제로 보지 않는다.
 */
export async function fetchResult(
  deps: FlowDeps,
  onError?: (error: ApiError) => void,
): Promise<VerifiedResult | undefined> {
  let text: string;
  try {
    text = await deps.api.client.resultText();
  } catch (error) {
    onError?.(error instanceof ApiError ? error : new ApiError(0, 'network_error'));
    return undefined;
  }
  try {
    return verifyResultText(text);
  } catch {
    return undefined;
  }
}

export async function submit(
  deps: FlowDeps,
  session: OnlineSessionState,
  input: { readonly ending: 'all_cleared' | 'early_exit'; readonly lastSeq: number },
  /** 완료 뒤 확정 결과 조회의 요청 실패 (TASK-2026-012 CR-01 REV-002). 화면이 통신 오류만 연결 점검에 넘긴다. */
  onResultError?: (error: ApiError) => void,
): Promise<SubmitOutcome> {
  if (session.response_version < 1) {
    // 응답이 없으면 서버가 완료를 확정하지 않는다. 먼저 저장해야 한다.
    return { kind: 'failed', code: 'response_missing', retriable: false };
  }
  try {
    const outcome = await deps.api.client.complete({
      ending: input.ending,
      last_seq: input.lastSeq,
      response_version: session.response_version,
    });
    if (!outcome.complete) {
      return { kind: 'incomplete', blocking: outcome.result.body.blocking_issues.map((issue) => issue.code) };
    }
    const next: OnlineSessionState = { ...session, submitted: true, answers: {} };
    writeSessionState(deps.storage, next);
    /*
     * complete 응답의 envelope을 그대로 쓰지 않고 `GET /result`로 다시 읽는다.
     * 화면이 보여 주고 내려받는 문서가 **저장된 확정본**과 같은 바이트임을 그
     * 경로로만 말할 수 있다(REV-007).
     */
    return { kind: 'complete', session: next, digest: outcome.result.digest, result: await fetchResult(deps, onResultError) };
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    if (api.status === 409 && api.code === 'already_completed') {
      /*
       * 같은 세션이 다른 값으로 이미 완료됐다. 참가자 입장에서는 끝난 것이고, 확정본은
       * 서버에 있다. 결과를 읽어 digest를 확인할 수 있으면 완료로 표시한다.
       */
      const stored = await fetchResult(deps, onResultError);
      if (stored === undefined) {
        return { kind: 'failed', code: api.code, retriable: false };
      }
      const next: OnlineSessionState = { ...session, submitted: true, answers: {} };
      writeSessionState(deps.storage, next);
      return { kind: 'complete', session: next, digest: stored.digest, result: stored };
    }
    return { kind: 'failed', code: api.code, retriable: api.retriable };
  }
}

export type LockedFinalOutcome =
  /** 서버가 잠근 최종 응답이 이 화면의 답과 같다. 그 version으로 이어 간다. */
  | { readonly kind: 'same'; readonly session: OnlineSessionState }
  /** 서버가 잠근 최종 응답이 이 화면의 답과 다르다. 이 화면의 제출로 확정하지 않는다. */
  | { readonly kind: 'different' }
  /** 서버 세션이 이 참여가 아니다. */
  | { readonly kind: 'mismatch' }
  | { readonly kind: 'failed'; readonly code: string };

/**
 * 서버가 잠근 최종 응답이 이 화면의 답과 같은지 **서버로** 확인한다 (TASK-2026-012 CR-01 REV-003).
 *
 * 이 기기 저장소는 증거가 되지 못한다. 세션 복구가 서버 version만 옮겨 적고 답은 예전 값을
 * 두며, 저장소 쓰기가 실패해도 응답 저장은 성공으로 끝나기 때문이다. 대신 서버의 기존 멱등
 * 규칙을 쓴다: 서버의 현재 version을 기준으로 **같은 최종 답**을 다시 보내면, 저장된 본문과
 * 바이트까지 같을 때만 `duplicate`로 성공하고 아니면 version 충돌로 거절된다. 새 API도 새로
 * 쓰는 자료도 없다(같으면 아무것도 바뀌지 않고, 다르면 거절된다).
 */
export async function confirmLockedFinal(
  deps: FlowDeps,
  session: OnlineSessionState,
  answers: Answers,
): Promise<LockedFinalOutcome> {
  try {
    const server = await deps.api.client.session();
    if (server.session_ref !== session.session_ref || server.campaign_id !== session.campaign_id) {
      return { kind: 'mismatch' };
    }
    deps.api.setCsrf(server.csrf_token);
    if (server.response_version < 1) {
      return { kind: 'different' };
    }
    const ack = await deps.api.client.putResponse({
      base_version: server.response_version - 1,
      final: true,
      answers: mergeAnswers(session, answers),
    });
    const next: OnlineSessionState = { ...session, response_version: ack.version };
    writeSessionState(deps.storage, next);
    return { kind: 'same', session: next };
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    if (api.code === 'response_version_conflict' || api.code === 'response_final_locked') {
      return { kind: 'different' };
    }
    return { kind: 'failed', code: api.code };
  }
}

export async function withdraw(deps: FlowDeps, session: OnlineSessionState): Promise<{ kind: 'withdrawn' } | { kind: 'failed'; code: string }> {
  try {
    await deps.api.client.withdraw();
    // 철회한 세션의 초안은 남기지 않는다.
    writeSessionState(deps.storage, { ...session, answers: {}, submitted: false });
    clearSessionState(deps.storage);
    return { kind: 'withdrawn' };
  } catch (error) {
    const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
    return { kind: 'failed', code: api.code };
  }
}
